import type { AgentType } from '@/lib/types/ai-agent';
import { normalizeFlowName } from '@/lib/agents/flow-defaults';

export interface SchedulingAgentSummary {
  id: string;
  name: string;
  agent_type: AgentType;
  flow_name: string;
}

export interface SchedulingPresentation {
  title: string;
  description: string;
  confirmationTitle: string;
  contextLabel: string;
}

export function toAgentSummary(raw: unknown): SchedulingAgentSummary | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : '';
  const name = typeof record.name === 'string' ? record.name : 'AI Agent';
  const agentType = record.agent_type === 'sales' ? 'sales' : 'interviewer';
  const flowName = normalizeFlowName(
    typeof record.flow_name === 'string' ? record.flow_name : undefined,
    agentType,
  );

  if (!id) {
    return null;
  }

  return {
    id,
    name,
    agent_type: agentType,
    flow_name: flowName,
  };
}

export function getSchedulingPresentation(
  agent: SchedulingAgentSummary | null,
  t: (key: string) => string,
): SchedulingPresentation {
  const isSales = agent?.agent_type === 'sales';
  if (isSales) {
    return {
      title: t('bookYourDemo'),
      description: t('bookDemoDesc'),
      confirmationTitle: t('demoBooked'),
      contextLabel: t('sales'),
    };
  }

  return {
    title: t('bookYourInterview'),
    description: t('bookInterviewDesc'),
    confirmationTitle: t('interviewBooked'),
    contextLabel: t('interview'),
  };
}

export function shouldApplyResponse(requestId: number, latestRequestId: number): boolean {
  return requestId === latestRequestId;
}
